// Pure rules for what the server says about the signed in person (GET /api/me). The browser only hides
// and greys; the Engine server enforces every one of these again.
import { DEFAULT_WORKSPACE_ID, workspaceLabel } from '@/config/workspace'

// Server role to the role names the menu data and shell already use.
const CONSOLE_ROLE = Object.freeze({ owner: 'admin', operator: 'operator', restricted_operator: 'viewer' })
export const LEAST_PRIVILEGE = 'viewer'
export const consoleRole = serverRole => CONSOLE_ROLE[serverRole] || LEAST_PRIVILEGE

export const GATE_LABEL = Object.freeze({ pull: 'Lead pulls', draft: 'Drafts', release: 'Batch release', spend: 'Spend' })
export const APOLLO_APPROVES = 'Apollo approves'
export const ROLE_WORD = Object.freeze({ owner: 'Owner', operator: 'Operator', restricted_operator: 'Restricted operator' })

// The workspace to work in: the person's pick when they hold it, else Clairvos when they hold it, else the first held.
export function pickWorkspace(me, wanted) {
  const held = me?.workspaces?.map(w => w.workspaceId) || []
  if (wanted && held.includes(wanted)) return wanted
  if (held.includes(DEFAULT_WORKSPACE_ID)) return DEFAULT_WORKSPACE_ID
  return held[0] ?? null
}

// What the person may see and do in the chosen workspace. Nothing about a workspace they do not hold.
export function deriveAccess(me, wanted) {
  const workspaceId = pickWorkspace(me, wanted)
  const entry = me?.workspaces?.find(w => w.workspaceId === workspaceId) || null
  const permissions = entry?.permissions || []
  const approvals = entry?.approvals || { pull: false, draft: false, release: false, spend: false }
  const workspaces = (me?.workspaces || []).map(w => ({ id: w.workspaceId, label: workspaceLabel(w.workspaceId), role: w.role }))
  const ownsAny = workspaces.some(w => w.role === 'owner')
  return {
    workspaceId,
    workspaceLabel: workspaceId ? workspaceLabel(workspaceId) : null,
    serverRole: entry?.role || null,
    role: consoleRole(entry?.role),
    permissions,
    approvals,
    workspaces,
    // The switcher is the owner's, and only when there is more than one workspace to switch between.
    canSwitch: ownsAny && workspaces.length > 1,
    can: code => permissions.includes(code),
    // Approval codes are never in `permissions`: a person holds a gate only through the approvals the server lists.
    canApprove: gate => approvals[gate] === true,
  }
}

// The workspace a list or import call names. Only a person who holds more than one needs to say which;
// one workspace is the server's own pick, and the claim is always one the person holds.
export function workspaceToSend(access) {
  if (!access || !access.workspaceId || access.workspaces.length < 2) return null
  return access.workspaces.some(w => w.id === access.workspaceId) ? access.workspaceId : null
}

// What a screen that needs access.admin shows for each state of the /api/me read.
export function accessGate({ meState, access }) {
  if (meState === 'loading') return 'loading'
  if (meState === 'notConnected') return 'notConnected'
  if (meState === 'error') return 'error'
  return access?.can('access.admin') ? 'allowed' : 'notAllowed'
}

// The role the menu, the shell and the greyed buttons read. The server's answer when it came, the old Clerk
// role only behind a not connected answer, and the least privilege while loading or after a failure.
export function effectiveRole({ meState, access, legacyRole }) {
  if (meState === 'ready') return access.role
  if (meState === 'notConnected') return legacyRole
  return LEAST_PRIVILEGE
}
